"use client";

/**
 * §11.4/§11.6 WCAG SC 3.3.7 Redundant Entry.
 *
 * "Three forms ask for an email: email-the-file, request-a-sound,
 * newsletter. Under 3.3.7 the second and third must not ask again in the
 * same session. The email is held in sessionStorage only, pre-filled, and
 * every pre-filled field carries a visible 'Using the address you entered
 * earlier — change it' link. Nothing is persisted past the session, so the
 * privacy promise in §05.6 still holds."
 *
 * sessionStorage (not localStorage) is deliberate: it clears itself when the
 * tab closes, so the pre-fill never survives past the session it was
 * entered in — no separate expiry logic needed.
 */

import { useCallback, useEffect, useState } from "react";

const SESSION_EMAIL_KEY = "tls-session-email";

function safeReadSessionEmail(): string {
  if (typeof window === "undefined") return "";
  try {
    return sessionStorage.getItem(SESSION_EMAIL_KEY) ?? "";
  } catch {
    // Private browsing / storage disabled — degrade to asking every time.
    return "";
  }
}

function safeWriteSessionEmail(email: string): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SESSION_EMAIL_KEY, email);
  } catch {
    // Quota exceeded / disabled storage — silent, matches announce.ts posture.
  }
}

export interface UseSessionEmailResult {
  /** Current field value — either what the user typed or the session pre-fill. */
  email: string;
  /** Controlled-input onChange handler. */
  setEmail: (value: string) => void;
  /**
   * True only while the field holds a value that came from sessionStorage
   * and the user has not yet chosen to change it. Drives the "Using the
   * address you entered earlier — change it" link.
   */
  isPrefilled: boolean;
  /** The "change it" link handler: clears the field and lets the user type a fresh address. */
  changeEmail: () => void;
  /** Call on successful submit so later forms in the same session pre-fill. */
  rememberEmail: (value: string) => void;
}

/**
 * Reads any address stored earlier in this session and pre-fills the field
 * with it. Call `rememberEmail(value)` after a successful submit so the
 * *next* form in the session sees it too — this hook does not write on
 * every keystroke, only on an explicit commit, so an abandoned partial edit
 * in one form never leaks into another.
 */
export function useSessionEmail(): UseSessionEmailResult {
  const [email, setEmailState] = useState("");
  const [isPrefilled, setIsPrefilled] = useState(false);

  useEffect(() => {
    const stored = safeReadSessionEmail();
    if (stored) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing from an external store (sessionStorage) read at mount, the documented external-system-sync case
      setEmailState(stored);
      // eslint-disable-next-line react-hooks/set-state-in-effect -- same external-system-sync as above
      setIsPrefilled(true);
    }
    // Runs once on mount — a value written mid-session by another form on
    // the same page is intentionally not picked up until the next mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setEmail = useCallback((value: string) => {
    setEmailState(value);
    setIsPrefilled(false);
  }, []);

  const changeEmail = useCallback(() => {
    setEmailState("");
    setIsPrefilled(false);
  }, []);

  const rememberEmail = useCallback((value: string) => {
    if (!value.trim()) return;
    safeWriteSessionEmail(value.trim());
  }, []);

  return { email, setEmail, isPrefilled, changeEmail, rememberEmail };
}
